import { useCallback, useEffect, useState } from "react";
import {
  Building2,
  KeyRound,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Settings,
  ShieldCheck,
  Trash2,
  UserCheck,
  UserMinus,
  Users,
  X,
} from "lucide-react";
import { api, ApiRequestError } from "../lib/api";
import type { OwnerOutlet, OwnerStaff } from "../lib/types";
import { PageHeader } from "../components/PageHeader";
import { StatusBadge } from "../components/StatusBadge";
import { formatDateTime, roleLabel } from "../lib/format";

type StaffDraft = {
  username: string;
  name: string;
  phone: string;
  password: string;
  role: string;
  outletId: string;
};

type StaffEditDraft = StaffDraft & {
  membershipId: number;
  status: string;
};

type PhoneCheckResponse = {
  phone: string;
  used: boolean;
  tenantMatches: Array<{
    membershipId: number;
    username: string;
    name: string;
    role: string | null;
    status: string | null;
  }>;
  otherTenantUsers: number;
  platform: { username: string; name: string } | null;
};

const emptyStaff: StaffDraft = {
  username: "",
  name: "",
  phone: "",
  password: "",
  role: "waiter",
  outletId: "",
};

function PhoneCheckNotice({
  check,
  loading,
  error,
}: {
  check: PhoneCheckResponse | null;
  loading: boolean;
  error: string | null;
}) {
  if (error) {
    return (
      <small className="phone-check phone-check--error" role="alert">
        {error}
      </small>
    );
  }
  if (loading) {
    return (
      <small className="phone-check" role="status">
        Memeriksa nomor WhatsApp...
      </small>
    );
  }
  if (!check?.used) return null;

  const owners = check.tenantMatches.map((match) => {
    const status = match.status === "active" ? "" : " · nonaktif";
    return `@${match.username} (${roleLabel(match.role ?? "")}${status})`;
  });
  if (check.platform) {
    owners.push(`akun platform @${check.platform.username}`);
  }
  if (check.otherTenantUsers > 0) {
    owners.push(`${check.otherTenantUsers} akun lain`);
  }
  const listed =
    owners.length > 3
      ? `${owners.slice(0, 3).join(", ")}, +${owners.length - 3} lagi`
      : owners.join(", ");

  return (
    <small className="phone-check phone-check--warning" role="status">
      Nomor sudah dipakai {listed}. Nomor tetap boleh disimpan, tetapi OTP dan
      akses akun bisa tercampur.
    </small>
  );
}

export function SettingsPage() {
  const [outlets, setOutlets] = useState<OwnerOutlet[]>([]);
  const [staff, setStaff] = useState<OwnerStaff[]>([]);
  const [outletId, setOutletId] = useState("");
  const [name, setName] = useState("");
  const [taxPercent, setTaxPercent] = useState("0");
  const [serviceChargePercent, setServiceChargePercent] = useState("0");
  const [paymentMode, setPaymentMode] = useState("per_order");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [staffDraft, setStaffDraft] = useState<StaffDraft | null>(null);
  const [staffEdit, setStaffEdit] = useState<StaffEditDraft | null>(null);
  const [creating, setCreating] = useState(false);
  const [savingStaff, setSavingStaff] = useState(false);
  const [phoneCheck, setPhoneCheck] = useState<PhoneCheckResponse | null>(null);
  const [phoneCheckLoading, setPhoneCheckLoading] = useState(false);
  const [phoneCheckError, setPhoneCheckError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [outletResponse, staffResponse] = await Promise.all([
        api<{ outlets: OwnerOutlet[] }>("/api/owner/outlets"),
        api<{ staff: OwnerStaff[] }>("/api/owner/staff"),
      ]);
      setOutlets(outletResponse.outlets);
      setStaff(staffResponse.staff);
      setError(null);
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat memuat pengaturan.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const activePhone = staffEdit?.phone ?? staffDraft?.phone ?? "";
  const activeMembershipId = staffEdit?.membershipId;

  useEffect(() => {
    const raw = activePhone.trim();
    setPhoneCheck(null);
    setPhoneCheckError(null);
    if (!raw) {
      setPhoneCheckLoading(false);
      return;
    }
    const digits = raw.replace(/\D/g, "");
    const normalized = digits.startsWith("0")
      ? `62${digits.slice(1)}`
      : digits.startsWith("8")
        ? `62${digits}`
        : digits;
    if (!/^62\d{8,13}$/.test(normalized)) {
      setPhoneCheckLoading(false);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setPhoneCheckLoading(true);
      try {
        const params = new URLSearchParams({ phone: raw });
        if (activeMembershipId) {
          params.set("membershipId", String(activeMembershipId));
        }
        const result = await api<PhoneCheckResponse>(
          `/api/owner/staff/phone-check?${params.toString()}`,
        );
        if (!cancelled) setPhoneCheck(result);
      } catch (requestError) {
        if (!cancelled) {
          setPhoneCheckError(
            requestError instanceof ApiRequestError
              ? requestError.message
              : "Gagal memeriksa nomor WhatsApp.",
          );
        }
      } finally {
        if (!cancelled) setPhoneCheckLoading(false);
      }
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [activePhone, activeMembershipId]);

  useEffect(() => {
    const selected = outlets.find((outlet) => outlet.id === outletId) ?? outlets[0];
    if (!selected) return;
    if (selected.id !== outletId) setOutletId(selected.id);
    setName(selected.name);
    setTaxPercent(String(selected.taxPercent));
    setServiceChargePercent(String(selected.serviceChargePercent));
    setPaymentMode(selected.paymentMode);
  }, [outletId, outlets]);

  async function saveOutlet() {
    const selected = outlets.find((outlet) => outlet.id === outletId);
    if (!selected) return;
    setSaving(true);
    setNotice(null);
    try {
      await api(`/api/owner/outlets/${selected.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: name.trim(),
          taxPercent: Number(taxPercent || 0),
          serviceChargePercent: Number(serviceChargePercent || 0),
          paymentMode,
        }),
      });
      setNotice("Pengaturan outlet disimpan.");
      await load();
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal menyimpan outlet.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function createStaff() {
    if (!staffDraft) return;
    setCreating(true);
    setError(null);
    try {
      await api("/api/owner/staff", {
        method: "POST",
        body: JSON.stringify({
          username: staffDraft.username.trim(),
          name: staffDraft.name.trim(),
          phone: staffDraft.phone.trim() || undefined,
          password: staffDraft.password,
          role: staffDraft.role,
          outletId: staffDraft.outletId || undefined,
        }),
      });
      setNotice(`Akun ${staffDraft.username} dibuat. Berikan password ke staf.`);
      setStaffDraft(null);
      await load();
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal membuat akun staf.",
      );
    } finally {
      setCreating(false);
    }
  }

  async function deactivateStaff(member: OwnerStaff) {
    if (!window.confirm(`Hapus akses ${member.name}? Sesi aktif akan dicabut.`)) {
      return;
    }
    try {
      await api(`/api/owner/staff/${member.membershipId}`, {
        method: "DELETE",
      });
      setNotice(`${member.name} dihapus dari workspace.`);
      await load();
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal menghapus akses staf.",
      );
    }
  }

  async function setStaffStatus(member: OwnerStaff, status: "active" | "inactive") {
    try {
      await api(`/api/owner/staff/${member.membershipId}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      setNotice(
        status === "active"
          ? `${member.name} diaktifkan kembali.`
          : `${member.name} disuspend.`,
      );
      await load();
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal mengubah status staf.",
      );
    }
  }

  function openStaffEdit(member: OwnerStaff) {
    setStaffEdit({
      membershipId: member.membershipId,
      username: member.username,
      name: member.name,
      phone: member.phone ?? "",
      password: "",
      role: member.role,
      outletId: member.outlet?.id ?? "",
      status: member.status,
    });
  }

  async function saveStaffEdit() {
    if (!staffEdit) return;
    setSavingStaff(true);
    setError(null);
    try {
      await api(`/api/owner/staff/${staffEdit.membershipId}`, {
        method: "PATCH",
        body: JSON.stringify({
          username: staffEdit.username.trim(),
          name: staffEdit.name.trim(),
          phone: staffEdit.phone.trim() || null,
          role: staffEdit.role,
          outletId: staffEdit.outletId || null,
          status: staffEdit.status,
          password: staffEdit.password || undefined,
        }),
      });
      setNotice(
        staffEdit.password
          ? `Password ${staffEdit.name} direset.`
          : `Data ${staffEdit.name} diperbarui.`,
      );
      setStaffEdit(null);
      await load();
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal menyimpan data staf.",
      );
    } finally {
      setSavingStaff(false);
    }
  }

  function randomPassword(): string {
    const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
    const values = crypto.getRandomValues(new Uint32Array(14));
    const password = Array.from(values)
      .map((value) => alphabet[value % alphabet.length])
      .join("");
    return `${password}!`;
  }

  function generatePassword() {
    if (!staffDraft) return;
    setStaffDraft({ ...staffDraft, password: randomPassword() });
  }

  function generateEditPassword() {
    if (!staffEdit) return;
    setStaffEdit({ ...staffEdit, password: randomPassword() });
  }

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Pengaturan"
        title="Outlet & staf"
        description="Atur pajak, service charge, mode pembayaran, dan akun staf yang boleh mengakses workspace."
        actions={
          <button type="button" className="btn btn-secondary" onClick={() => void load()}>
            <RefreshCw size={16} /> Muat ulang
          </button>
        }
      />

      {error ? <div className="alert alert--danger">{error}</div> : null}
      {notice ? <div className="alert alert--success">{notice}</div> : null}

      {loading ? (
        <div className="empty-state empty-state--roomy">
          <Settings size={32} />
          <h3>Memuat pengaturan...</h3>
        </div>
      ) : (
        <div className="settings-grid">
          <section className="card card--pad">
            <header className="report-card__head">
              <div>
                <p className="eyebrow">Outlet</p>
                <h2>Konfigurasi operasional</h2>
              </div>
              <Building2 size={20} />
            </header>
            <div className="form-grid">
              <label className="field field--full">
                <span>Outlet</span>
                <select
                  className="input"
                  value={outletId}
                  onChange={(event) => setOutletId(event.target.value)}
                >
                  {outlets.map((outlet) => (
                    <option key={outlet.id} value={outlet.id}>
                      {outlet.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field field--full">
                <span>Nama outlet</span>
                <input
                  className="input"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </label>
              <label className="field">
                <span>Pajak (%)</span>
                <input
                  className="input"
                  inputMode="decimal"
                  value={taxPercent}
                  onChange={(event) => setTaxPercent(event.target.value)}
                />
              </label>
              <label className="field">
                <span>Service charge (%)</span>
                <input
                  className="input"
                  inputMode="decimal"
                  value={serviceChargePercent}
                  onChange={(event) => setServiceChargePercent(event.target.value)}
                />
              </label>
              <label className="field field--full">
                <span>Mode pembayaran</span>
                <select
                  className="input"
                  value={paymentMode}
                  onChange={(event) => setPaymentMode(event.target.value)}
                >
                  <option value="per_order">Bayar per pesanan</option>
                  <option value="open_bill">Open bill (fase berikutnya)</option>
                </select>
              </label>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={saving}
                onClick={() => void saveOutlet()}
              >
                <Save size={16} /> {saving ? "Menyimpan..." : "Simpan outlet"}
              </button>
            </div>
            <p className="fine-print">
              Pajak dan service charge dihitung server saat pesanan dibuat, bukan di
              browser. Nilai 0 berarti tidak dikenakan.
            </p>
          </section>

          <section className="card card--pad">
            <header className="report-card__head">
              <div>
                <p className="eyebrow">Akses</p>
                <h2>Staf & role</h2>
              </div>
              <Users size={20} />
            </header>

            <div className="staff-list">
              {staff.map((member) => (
                <article className="staff-row" key={member.membershipId}>
                  <div className="staff-row__avatar">
                    {member.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="staff-row__body">
                    <strong>{member.name}</strong>
                    <span>
                      @{member.username} · {roleLabel(member.role)}
                      {member.outlet ? ` · ${member.outlet.name}` : " · semua outlet"}
                    </span>
                    <small>
                      {member.lastLoginAt
                        ? `Login terakhir ${formatDateTime(member.lastLoginAt)}`
                        : "Belum pernah login"}
                    </small>
                  </div>
                  <div className="staff-row__meta">
                    <StatusBadge
                      status={member.status}
                      label={member.status === "active" ? "Aktif" : "Nonaktif"}
                    />
                    {member.role !== "owner" ? (
                      <div className="staff-row__actions">
                        <button
                          type="button"
                          className="icon-button"
                          title="Edit / reset password"
                          onClick={() => openStaffEdit(member)}
                        >
                          <Pencil size={15} />
                        </button>
                        {member.status === "active" ? (
                          <button
                            type="button"
                            className="icon-button"
                            title="Suspend"
                            onClick={() => void setStaffStatus(member, "inactive")}
                          >
                            <UserMinus size={15} />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="icon-button"
                            title="Aktifkan"
                            onClick={() => void setStaffStatus(member, "active")}
                          >
                            <UserCheck size={15} />
                          </button>
                        )}
                        <button
                          type="button"
                          className="icon-button staff-row__remove"
                          title="Hapus akses"
                          onClick={() => void deactivateStaff(member)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setStaffDraft({ ...emptyStaff, outletId: outlets[0]?.id ?? "" })}
            >
              <Plus size={16} /> Tambah staf
            </button>
            <p className="fine-print">
              Staf dapur dan pelayan tidak melihat nominal pesanan. Owner dan
              supervisor dapat melihat total. Owner dapat mengedit, mereset
              password, menyuspend, atau menghapus akses staf. Staf juga dapat
              mengganti password sendiri dari menu Profil.
            </p>
          </section>
        </div>
      )}

      {staffDraft ? (
        <div className="sheet-backdrop" role="presentation" onClick={() => setStaffDraft(null)}>
          <div
            className="sheet sheet--form"
            role="dialog"
            aria-modal="true"
            aria-label="Tambah staf"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sheet__head">
              <div>
                <p className="eyebrow">Akses</p>
                <h2>Tambah staf</h2>
              </div>
              <button type="button" className="icon-button" onClick={() => setStaffDraft(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="sheet__body">
              <div className="form-grid">
                <label className="field">
                  <span>Nama</span>
                  <input
                    className="input"
                    autoFocus
                    value={staffDraft.name}
                    onChange={(event) => setStaffDraft({ ...staffDraft, name: event.target.value })}
                  />
                </label>
                <label className="field">
                  <span>Username</span>
                  <input
                    className="input"
                    autoCapitalize="none"
                    value={staffDraft.username}
                    onChange={(event) =>
                      setStaffDraft({ ...staffDraft, username: event.target.value })
                    }
                    placeholder="dapur01"
                  />
                </label>
                <label className="field">
                  <span>Nomor WhatsApp</span>
                  <input
                    className="input"
                    inputMode="tel"
                    value={staffDraft.phone}
                    onChange={(event) => setStaffDraft({ ...staffDraft, phone: event.target.value })}
                    placeholder="08xxxxxxxxxx"
                  />
                  <PhoneCheckNotice
                    check={phoneCheck}
                    loading={phoneCheckLoading}
                    error={phoneCheckError}
                  />
                </label>
                <label className="field">
                  <span>Role</span>
                  <select
                    className="input"
                    value={staffDraft.role}
                    onChange={(event) => setStaffDraft({ ...staffDraft, role: event.target.value })}
                  >
                    <option value="admin">Admin</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="cashier">Kasir</option>
                    <option value="waiter">Pelayan</option>
                    <option value="kitchen">Dapur</option>
                  </select>
                </label>
                <label className="field field--full">
                  <span>Outlet</span>
                  <select
                    className="input"
                    value={staffDraft.outletId}
                    onChange={(event) =>
                      setStaffDraft({ ...staffDraft, outletId: event.target.value })
                    }
                  >
                    <option value="">Semua outlet</option>
                    {outlets.map((outlet) => (
                      <option key={outlet.id} value={outlet.id}>
                        {outlet.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field field--full">
                  <span>Password awal</span>
                  <span className="input-group">
                    <input
                      className="input"
                      type="text"
                      value={staffDraft.password}
                      onChange={(event) =>
                        setStaffDraft({ ...staffDraft, password: event.target.value })
                      }
                      minLength={8}
                      placeholder="Minimal 8 karakter"
                    />
                    <button
                      type="button"
                      className="input-group__action"
                      onClick={generatePassword}
                      title="Buat password acak"
                    >
                      <KeyRound size={16} />
                    </button>
                  </span>
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setStaffDraft(null)}>
                  Batal
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={creating || !staffDraft.name || !staffDraft.username || staffDraft.password.length < 8}
                  onClick={() => void createStaff()}
                >
                  <ShieldCheck size={16} /> Buat akun
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {staffEdit ? (
        <div className="sheet-backdrop" role="presentation" onClick={() => setStaffEdit(null)}>
          <div
            className="sheet sheet--form"
            role="dialog"
            aria-modal="true"
            aria-label="Edit staf"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sheet__head">
              <div>
                <p className="eyebrow">Akses</p>
                <h2>Edit staf</h2>
              </div>
              <button type="button" className="icon-button" onClick={() => setStaffEdit(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="sheet__body">
              <div className="form-grid">
                <label className="field">
                  <span>Nama</span>
                  <input
                    className="input"
                    value={staffEdit.name}
                    onChange={(event) =>
                      setStaffEdit({ ...staffEdit, name: event.target.value })
                    }
                  />
                </label>
                <label className="field">
                  <span>Username</span>
                  <input
                    className="input"
                    autoCapitalize="none"
                    value={staffEdit.username}
                    onChange={(event) =>
                      setStaffEdit({ ...staffEdit, username: event.target.value })
                    }
                  />
                </label>
                <label className="field">
                  <span>Nomor WhatsApp</span>
                  <input
                    className="input"
                    inputMode="tel"
                    value={staffEdit.phone}
                    onChange={(event) =>
                      setStaffEdit({ ...staffEdit, phone: event.target.value })
                    }
                  />
                  <PhoneCheckNotice
                    check={phoneCheck}
                    loading={phoneCheckLoading}
                    error={phoneCheckError}
                  />
                </label>
                <label className="field">
                  <span>Role</span>
                  <select
                    className="input"
                    value={staffEdit.role}
                    onChange={(event) =>
                      setStaffEdit({ ...staffEdit, role: event.target.value })
                    }
                  >
                    <option value="admin">Admin</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="cashier">Kasir</option>
                    <option value="waiter">Pelayan</option>
                    <option value="kitchen">Dapur</option>
                  </select>
                </label>
                <label className="field">
                  <span>Status</span>
                  <select
                    className="input"
                    value={staffEdit.status}
                    onChange={(event) =>
                      setStaffEdit({ ...staffEdit, status: event.target.value })
                    }
                  >
                    <option value="active">Aktif</option>
                    <option value="inactive">Suspend</option>
                  </select>
                </label>
                <label className="field field--full">
                  <span>Outlet</span>
                  <select
                    className="input"
                    value={staffEdit.outletId}
                    onChange={(event) =>
                      setStaffEdit({ ...staffEdit, outletId: event.target.value })
                    }
                  >
                    <option value="">Semua outlet</option>
                    {outlets.map((outlet) => (
                      <option key={outlet.id} value={outlet.id}>
                        {outlet.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field field--full">
                  <span>Password baru (opsional)</span>
                  <span className="input-group">
                    <input
                      className="input"
                      type="text"
                      value={staffEdit.password}
                      onChange={(event) =>
                        setStaffEdit({ ...staffEdit, password: event.target.value })
                      }
                      minLength={8}
                      placeholder="Kosongkan jika tidak diubah"
                    />
                    <button
                      type="button"
                      className="input-group__action"
                      onClick={generateEditPassword}
                      title="Buat password acak"
                    >
                      <KeyRound size={16} />
                    </button>
                  </span>
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setStaffEdit(null)}>
                  Batal
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={
                    savingStaff ||
                    !staffEdit.name.trim() ||
                    !staffEdit.username.trim() ||
                    (staffEdit.password.length > 0 && staffEdit.password.length < 8)
                  }
                  onClick={() => void saveStaffEdit()}
                >
                  <Save size={16} /> {savingStaff ? "Menyimpan..." : "Simpan"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
